import type { Dictionary } from '../i18n/en';
import { ceilDiv } from '../lib/decimal';
import { isApiError } from './client';
import type { ApiIssue } from './types';

/**
 * Turns API errors into readable, localized text. Error codes and issue keys are stable API
 * identifiers; each one has a message in both dictionaries (unknown keys get a generic fallback
 * that still names the key, so nothing is silently swallowed).
 */

export function interpolate(
  template: string,
  params: Record<string, string | number> = {},
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  );
}

export function issueMessage(t: Dictionary, issue: ApiIssue): string {
  const template = (t.issues as Record<string, string | undefined>)[issue.issue];
  return template
    ? interpolate(template, issue.params)
    : interpolate(t.errors.issueFallback, { issue: issue.issue });
}

export function retryAfterText(t: Dictionary, seconds: number): string {
  return seconds >= 90
    ? interpolate(t.errors.retryInMinutes, { minutes: ceilDiv(seconds, 60) })
    : interpolate(t.errors.retryInSeconds, { seconds });
}

export function errorMessage(t: Dictionary, error: unknown): string {
  if (!isApiError(error)) return t.errors.unknown;
  const message = (t.errors.codes as Record<string, string | undefined>)[error.code];
  if (error.code === 'rate_limited' && error.retryAfterSeconds !== undefined)
    return `${t.errors.codes.rate_limited} ${retryAfterText(t, error.retryAfterSeconds)}`;
  return message ?? t.errors.unknown;
}

export function requestIdOf(error: unknown): string | undefined {
  return isApiError(error) ? error.requestId : undefined;
}

export function issuesOf(error: unknown): ApiIssue[] {
  return isApiError(error) && error.code === 'validation_error' ? error.issues : [];
}

/** First message per issue path, e.g. { 'taxId': '…', 'address.street': '…' }. */
export function fieldErrors(t: Dictionary, issues: ApiIssue[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) out[issue.path] ??= issueMessage(t, issue);
  return out;
}
